import type {
  UnmappedProductRow,
  UtilizationGroup,
  UtilizationGroupRow,
  UtilizationProductRow,
  UtilizationReportData,
} from './utilizationReport';

export interface RestaurantUtilizationReport {
  restaurantId: string;
  restaurantName: string;
  data: UtilizationReportData;
}

export interface RestaurantUtilizationBreakdown<T> {
  restaurantId: string;
  restaurantName: string;
  row: T;
}

export interface OrganizationUtilizationProductRow extends UtilizationProductRow {
  /** Sum of positive local waste: a shortage elsewhere must never cancel it. */
  potentialWasteEur: number;
  hasOverOrdering: boolean;
  hasUnderOrdering: boolean;
  /** Local numbers with qualified recipe names, for accurate recipe chart attribution. */
  restaurantContributions?: UtilizationProductRow[];
}

export interface OrganizationUtilizationReportData extends UtilizationReportData {
  rows: OrganizationUtilizationProductRow[];
  restaurantBreakdowns: Map<string, RestaurantUtilizationBreakdown<UtilizationProductRow>[]>;
  unmappedBreakdowns: Map<string, RestaurantUtilizationBreakdown<UnmappedProductRow>[]>;
}

/** Stable across periods and restaurant coverage, including when a second unit appears.
 * Retain the platform:: prefix accepted by the favorites API. */
function organizationKey(row: Pick<UnmappedProductRow, 'key' | 'side' | 'unknownSupplierName'>, unit: string): string {
  return `${row.key}::org:${encodeURIComponent(JSON.stringify([
    unit,
    row.side,
    (row.unknownSupplierName ?? '').trim().toLowerCase(),
  ]))}`;
}

const distinct = (values: string[]): string[] => Array.from(new Set(values)).sort();

/** Combine already-computed restaurant reports, never their inputs. Recipe allocation,
 * exclusions, waste settings and valuation therefore stay local to each restaurant. */
export function mergeOrganizationUtilization(
  reports: RestaurantUtilizationReport[]
): OrganizationUtilizationReportData {
  const restaurantBreakdowns: OrganizationUtilizationReportData['restaurantBreakdowns'] = new Map();
  const unmappedBreakdowns: OrganizationUtilizationReportData['unmappedBreakdowns'] = new Map();
  const rowsByKey = new Map<string, OrganizationUtilizationProductRow>();
  const unmappedByKey = new Map<string, UnmappedProductRow>();
  const labels = new Map(reports.map((report) => [report.restaurantId,
    reports.some((other) => other.restaurantId !== report.restaurantId && other.restaurantName === report.restaurantName)
      ? `${report.restaurantName} (${report.restaurantId})` : report.restaurantName,
  ]));
  const nameFor = (report: RestaurantUtilizationReport, name: string): string =>
    `${labels.get(report.restaurantId)} · ${name}`;

  for (const report of reports) {
    const names = (values: string[]) => values.map((name) => nameFor(report, name));
    for (const row of report.data.rows) {
      const key = organizationKey(row, row.stdUnit);
      const breakdown = restaurantBreakdowns.get(key) ?? [];
      breakdown.push({ restaurantId: report.restaurantId, restaurantName: report.restaurantName, row });
      restaurantBreakdowns.set(key, breakdown);
      const dishes = names(row.dishes);
      const subrecipes = names(row.subrecipes);
      const dishesViaAlternative = names(row.dishesViaAlternative);
      const dishBreakdown = row.dishBreakdown.map((dish) => ({
        ...dish,
        dishName: nameFor(report, dish.dishName),
        prepPaths: dish.prepPaths?.map((path) => ({
          ...path,
          hops: path.hops.map((hop) => ({ ...hop, name: nameFor(report, hop.name) })),
        })),
      }));
      const potentialWasteEur = Math.max(0, row.wasteEur ?? 0);
      const hasOverOrdering = row.wasteBase > 0 && potentialWasteEur > 0;
      const hasUnderOrdering = row.wasteBase < 0;
      const contribution = { ...row, dishes, subrecipes, dishesViaAlternative, dishBreakdown };
      const existing = rowsByKey.get(key);
      if (existing) {
        existing.spend += row.spend;
        existing.orderedBase += row.orderedBase;
        existing.neededBase += row.neededBase;
        existing.wasteBase += row.wasteBase;
        existing.wasteEur = existing.wasteEur === null && row.wasteEur === null
          ? null : (existing.wasteEur ?? 0) + (row.wasteEur ?? 0);
        existing.potentialWasteEur += potentialWasteEur;
        existing.hasOverOrdering ||= hasOverOrdering;
        existing.hasUnderOrdering ||= hasUnderOrdering;
        // There is no single latest price across independent purchasing locations.
        if (existing.pricePerBase !== row.pricePerBase) existing.pricePerBase = null;
        existing.dishes.push(...dishes);
        existing.subrecipes.push(...subrecipes);
        existing.dishesViaAlternative.push(...dishesViaAlternative);
        existing.alternativeForNames.push(...row.alternativeForNames);
        existing.dishBreakdown.push(...dishBreakdown);
        existing.recentOrders.push(...row.recentOrders);
        existing.receivedViaAlternative ||= row.receivedViaAlternative;
        existing.restaurantContributions?.push(contribution);
      } else {
        rowsByKey.set(key, {
          ...row, key, dishes: [...dishes], subrecipes: [...subrecipes],
          dishesViaAlternative: [...dishesViaAlternative], dishBreakdown: [...dishBreakdown],
          alternativeForNames: [...row.alternativeForNames],
          recentOrders: [...row.recentOrders],
          potentialWasteEur, hasOverOrdering, hasUnderOrdering,
          restaurantContributions: [contribution],
        });
      }
    }
    // Unmapped purchases remain separate even if another venue mapped the product.
    for (const row of report.data.unmappedRows) {
      const key = organizationKey(row, '');
      const breakdown = unmappedBreakdowns.get(key) ?? [];
      breakdown.push({ restaurantId: report.restaurantId, restaurantName: report.restaurantName, row });
      unmappedBreakdowns.set(key, breakdown);
      const existing = unmappedByKey.get(key);
      if (existing) existing.spend += row.spend;
      else unmappedByKey.set(key, { ...row, key });
    }
  }

  const rows = Array.from(rowsByKey.values());
  for (const row of rows) {
    row.utilizationPercent = row.orderedBase > 0 ? row.neededBase / row.orderedBase * 100 : null;
    row.dishes = distinct(row.dishes);
    row.subrecipes = distinct(row.subrecipes);
    row.dishesViaAlternative = distinct(row.dishesViaAlternative);
    row.alternativeForNames = distinct(row.alternativeForNames);
    row.recentOrders.sort((a, b) => new Date(b.orderDate).getTime() - new Date(a.orderDate).getTime());
    row.recentOrders = row.recentOrders.slice(0, 5);
  }
  rows.sort((a, b) => b.potentialWasteEur - a.potentialWasteEur);
  const unmappedRows = Array.from(unmappedByKey.values()).sort((a, b) => b.spend - a.spend);
  const groupsFor = (kind: 'grouped' | 'pooled'): UtilizationGroupRow[] => reports.flatMap((report) =>
    report.data[kind].map((group) => ({
      ...group,
      signature: `${report.restaurantId}::${group.signature}`,
      primaryName: nameFor(report, group.primaryName),
      dishes: group.dishes.map((name) => nameFor(report, name)),
      subrecipes: group.subrecipes.map((name) => nameFor(report, name)),
      members: group.members.map((member) => {
        const key = organizationKey(member.row ?? { ...member, side: group.side }, member.row?.stdUnit ?? member.stdUnit);
        return { ...member, key, row: member.row ? rowsByKey.get(key) : undefined };
      }),
    })));
  const sumGroup = (field: 'noQuantityInfoFood' | 'noQuantityInfoDrinks'): UtilizationGroup =>
    reports.reduce((sum, report) => ({
      count: sum.count + report.data[field].count,
      spend: sum.spend + report.data[field].spend,
    }), { count: 0, spend: 0 });
  const unmappedGroup = (side: 'food' | 'drinks'): UtilizationGroup => {
    const selected = unmappedRows.filter((row) => row.side === side);
    return { count: selected.length, spend: selected.reduce((sum, row) => sum + row.spend, 0) };
  };
  const totalPotentialWasteEur = rows.reduce((sum, row) => sum + row.potentialWasteEur, 0);
  const periodSpend = reports.reduce((sum, report) => sum + report.data.periodSpend, 0);
  return {
    rows,
    restaurantBreakdowns,
    unmappedBreakdowns,
    grouped: groupsFor('grouped'),
    pooled: groupsFor('pooled'),
    hasMappableCostPrices: reports.some((report) => report.data.hasMappableCostPrices),
    unmappedRows,
    unmappedFood: unmappedGroup('food'),
    unmappedDrinks: unmappedGroup('drinks'),
    noQuantityInfoFood: sumGroup('noQuantityInfoFood'),
    noQuantityInfoDrinks: sumGroup('noQuantityInfoDrinks'),
    totalPotentialWasteEur,
    periodSpend,
    spendBySide: {
      food: reports.reduce((sum, report) => sum + report.data.spendBySide.food, 0),
      drinks: reports.reduce((sum, report) => sum + report.data.spendBySide.drinks, 0),
    },
    wastePercentOfSpend: periodSpend > 0 ? totalPotentialWasteEur / periodSpend * 100 : 0,
    overOrderedCount: rows.filter((row) => row.hasOverOrdering).length,
    suppliers: distinct(reports.flatMap((report) => report.data.suppliers)),
    categories: distinct(reports.flatMap((report) => report.data.categories)),
    dishNames: distinct(rows.flatMap((row) => row.dishes)),
    subrecipeNames: distinct(rows.flatMap((row) => row.subrecipes)),
    comparisonHidden: reports.some((report) => report.data.comparisonHidden),
  };
}
